import { Alert, Box, Button, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Product } from "../api/client";
import { deleteProduct } from "../api/management";
import { Panel } from "../components/layout/Panel";
import { CompGrid, type Column } from "../components/grid/CompGrid";
import { Link as MuiLink } from "@mui/material";
import { useHierarchyContext } from "../contexts/HierarchyContext";

export function PageTenant() {
  const { tenant } = useParams<{ tenant: string }>();
  const tenantSlug = tenant ?? null;
  const { products: allProducts, loading, error } = useHierarchyContext();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const products = useMemo(
    () => (tenantSlug ? allProducts.filter((p) => p.tenantSlug === tenantSlug) : []),
    [allProducts, tenantSlug],
  );

  if (!tenantSlug) {
    return <Alert severity="warning">Missing tenant in URL. Please navigate from Home.</Alert>;
  }

  const columns: Column<Product>[] = [
    {
      id: "slug",
      label: "Slug",
      sortable: true,
      sortValue: (p) => p.slug,
      render: (p) => (
        <MuiLink
          component={RouterLink}
          to={`/products/${encodeURIComponent(p.slug)}/services`}
          underline="hover"
        >
          <Typography variant="body2" className="om-text-mono">
            {p.slug}
          </Typography>
        </MuiLink>
      ),
    },
    {
      id: "name",
      label: "Name",
      sortable: true,
      sortValue: (p) => p.name,
      render: (p) => <Typography variant="body2">{p.name}</Typography>,
    },
    {
      id: "description",
      label: "Description",
      render: (p) => (
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {p.description || "-"}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 4 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/tenants/${encodeURIComponent(tenantSlug)}/products/new`}
            disabled={loading}
          >
            Create
          </Button>
        </Box>
      ),
      render: (t) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mx: 4 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/products/${encodeURIComponent(t.slug)}/services`}
          >
            Open
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            disabled={loading}
            onClick={async () => {
              if (!window.confirm(`Delete product "${t.name || t.slug}"? This will also delete all services under it.`)) return;
              setActionError(null);
              try {
                await deleteProduct(t.slug, undefined);
                await queryClient.refetchQueries({ queryKey: ["hierarchy", "full"] });
              } catch (err) {
                setActionError(err instanceof Error ? err.message : "Failed to delete product");
              }
            }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <Panel title="Products">
        <CompGrid
          items={products}
          loading={loading}
          error={error?.message ?? null}
          emptyMessage="No products yet."
          columns={columns}
          keyExtractor={(p) => p.slug}
        />
      </Panel>
    </Box>
  );
}
